import { useEffect, useMemo, useState } from "react";
import { Select, Tag } from "antd";
import { useTranslation } from "react-i18next";

import { Body } from "../../../../components/main-body/body";
import PageContentLoader from "../../../../components/loader/page-content-loader";
import useNotification from "../../../../components/notification/notification";
import ChartRenderer from "../reports-ai-core/ChartRenderer";
import ApiAnalytics from "../../../../api/analytics";

import "./usage-analytics.css";

const PERIOD_OPTIONS = ["7d", "30d", "90d"];
const DEFAULT_PERIOD = "30d";

const UsageAnalytics = () => {
  const { t } = useTranslation();
  const { openNotification } = useNotification();

  const [period, setPeriod] = useState(DEFAULT_PERIOD);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isCurrent = true;
    setLoading(true);

    ApiAnalytics.getUsageAnalytics(period)
      .then((response) => {
        if (!isCurrent) return;
        setData(response.data || null);
      })
      .catch((error) => {
        console.error("Load usage analytics error:", error);

        if (!isCurrent) return;
        setData(null);
        openNotification?.({
          type: "error",
          message: t("analytics.usage_dashboard.load_error"),
        });
      })
      .finally(() => {
        if (isCurrent) setLoading(false);
      });

    return () => {
      isCurrent = false;
    };
  }, [period]);

  // Environment is resolved server-side (from the deployment's own host) and echoed back -
  // the dashboard only ever shows the environment it is deployed in, so it is a read-only
  // label, not a user choice.
  const environment = data?.environment || null;

  const dau = Array.isArray(data?.dau) ? data.dau : [];
  const topScreens = Array.isArray(data?.topScreens) ? data.topScreens : [];

  // Two blocks that arrive in this response are rendered elsewhere. `generic` (pre-sign-in
  // activity) went to the Onboarding page in LF360-1917 - it carries no project, so it never
  // belonged among these project-scoped figures. `funnel` (scenario drop-off) went to the
  // Scenarios page in LF360-1909 - progress through a scenario is not a question about
  // whether the app works, and its completion figure competed with programme reporting.
  // See PreSignInActivity and ScenarioDropOff.
  const dauHasData = dau.length > 0;
  const topScreensHasData = topScreens.length > 0;

  const dauChartSpec = useMemo(
    () => ({
      type: "area",
      data: dau,
      config: {
        xAxis: { dataKey: "day", label: t("analytics.usage_dashboard.dau.x_label") },
        yAxis: { dataKey: "dau", label: t("analytics.usage_dashboard.dau.y_label") },
      },
    }),
    [dau, t]
  );

  const topScreensChartSpec = useMemo(
    () => ({
      type: "bar",
      data: topScreens,
      config: {
        xAxis: { dataKey: "screen", label: t("analytics.usage_dashboard.top_screens.x_label") },
        yAxis: { dataKey: "views", label: t("analytics.usage_dashboard.top_screens.y_label") },
      },
    }),
    [topScreens, t]
  );

  return (
    <Body
      header={{
        title: t("analytics.usage"),
        period: (
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            {environment && (
              <Tag color="blue" style={{ margin: 0 }}>
                {t("analytics.usage_dashboard.environment_label")}:{" "}
                {t(`analytics.usage_dashboard.env.${environment}`, {
                  defaultValue: environment,
                })}
              </Tag>
            )}
            <Select
              value={period}
              onChange={setPeriod}
              style={{ width: 160 }}
              options={PERIOD_OPTIONS.map((value) => ({
                value,
                label: t(`analytics.usage_dashboard.period.${value}`),
              }))}
            />
          </div>
        ),
      }}
      scroll
    >
      <div className="usage-analytics" style={{ position: "relative" }}>
        {loading && <PageContentLoader />}

        <section className="usage-analytics__section">
          <h3 className="usage-analytics__title">{t("analytics.usage_dashboard.dau.title")}</h3>

          {dauHasData ? (
            <ChartRenderer chartSpec={dauChartSpec} />
          ) : (
            <p className="usage-analytics__empty">{t("analytics.usage_dashboard.empty")}</p>
          )}
        </section>

        <section className="usage-analytics__section">
          <h3 className="usage-analytics__title">
            {t("analytics.usage_dashboard.top_screens.title")}
          </h3>

          {topScreensHasData ? (
            <ChartRenderer chartSpec={topScreensChartSpec} />
          ) : (
            <p className="usage-analytics__empty">{t("analytics.usage_dashboard.empty")}</p>
          )}
        </section>

      </div>
    </Body>
  );
};

export default UsageAnalytics;
